if (!customElements.get('wh-collection-search')) {
  customElements.define('wh-collection-search', class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.clearButton = this.querySelector('[data-clear]');
      this.grid = document.getElementById('ProductGridContainer');
      this.results = document.getElementById(this.dataset.results);
      if (!this.grid || !this.results) return;
      this.status = this.results.querySelector('[role="status"]');
      this.list = this.results.querySelector('ul');
      this.more = this.results.querySelector('[data-more]');
      this.cache = new Map();
      this.generation = 0;
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.querySelector('form').addEventListener('submit', (event) => {
        event.preventDefault();
        clearTimeout(this.timer);
        this.search();
      }, options);
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.generation++;
        if (!this.input.value.trim()) this.search();
        else this.timer = setTimeout(() => this.search(), 250);
      }, options);
      this.clearButton.addEventListener('click', () => {
        this.input.value = '';
        this.search();
        this.input.focus();
      }, options);
      this.more.addEventListener('click', () => this.showMore(), options);
      // Native facets replace the grid after filters, sorting or browser history change.
      this.observer = new MutationObserver(() => {
        if (this.input.value.trim()) this.search();
      });
      this.observer.observe(this.grid, { childList: true });
      this.hidden = false;
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.generation++;
      this.listeners?.abort();
      this.observer?.disconnect();
    }
    normalize(value) {
      return value.toLocaleLowerCase('de').replace(/ß/g, 'ss').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ae|oe|ue/g, (pair) => pair[0]);
    }
    editDistance(left, right) {
      const rows = Array.from({ length: left.length + 1 }, (_, i) => [i]);
      for (let j = 0; j <= right.length; j++) rows[0][j] = j;
      for (let i = 1; i <= left.length; i++) {
        for (let j = 1; j <= right.length; j++) {
          rows[i][j] = Math.min(
            rows[i - 1][j] + 1,
            rows[i][j - 1] + 1,
            rows[i - 1][j - 1] + (left[i - 1] === right[j - 1] ? 0 : 1)
          );
          if (i > 1 && j > 1 && left[i - 1] === right[j - 2] && left[i - 2] === right[j - 1]) {
            rows[i][j] = Math.min(rows[i][j], rows[i - 2][j - 2] + 1);
          }
        }
      }
      return rows[left.length][right.length];
    }
    matchScore(terms, words) {
      let normalized = this.normalize(terms);
      if (normalized.includes('bayerischer gebirgsschweisshund')) normalized += ' bgs';
      if (normalized.includes('kleiner munsterlander')) normalized += ' klm';
      if (normalized.includes('dackel')) normalized += ' teckel';
      const tokens = normalized.match(/[a-z0-9]+/g) || [];
      let score = 0;
      for (const word of words) {
        if (normalized.includes(word)) continue;
        // Short words stay exact; longer words allow one or two typing errors.
        const tolerance = word.length < 4 ? 0 : word.length < 8 ? 1 : 2;
        if (!tolerance) return Infinity;
        let best = Infinity;
        for (const token of tokens) {
          for (let length = Math.max(1, word.length - tolerance); length <= word.length + tolerance; length++) {
            if (token.length < length) continue;
            // Prefixes and suffixes also cover German compounds such as Rauhaardackel.
            for (const part of [token.slice(0, length), token.slice(-length)]) {
              best = Math.min(best, this.editDistance(word, part));
            }
          }
          if (best <= 1) break;
        }
        if (best > tolerance) return Infinity;
        score += best;
      }
      return score;
    }
    async products(url) {
      const key = url.toString();
      if (this.cache.has(key)) return this.cache.get(key);
      const products = [];
      const visited = new Set();
      let next = url;
      while (next) {
        if (visited.has(next.toString())) throw new Error('Repeated index page');
        visited.add(next.toString());
        const response = await fetch(next);
        if (!response.ok) throw new Error('Index unavailable');
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const node = document.querySelector('[data-collection-search-index]');
        if (!node) throw new Error('Index missing');
        const data = JSON.parse(node.textContent);
        products.push(...data.products);
        next = data.next ? new URL(data.next, url) : null;
        if (next) {
          if (next.origin !== url.origin || next.pathname !== url.pathname) throw new Error('Invalid next page');
          next.searchParams.set('section_id', 'wh-collection-search-index');
        }
      }
      this.cache.set(key, products);
      return products;
    }
    async search() {
      const generation = ++this.generation;
      const query = this.input.value.trim();
      this.clearButton.hidden = !query;
      this.results.hidden = !query;
      this.grid.hidden = Boolean(query);
      this.list.replaceChildren();
      this.more.hidden = true;
      if (!query) return;
      this.status.textContent = window.whThemeText('wh_collection_search_loading');
      this.results.setAttribute('aria-busy', 'true');
      const url = new URL(location.href);
      url.hash = '';
      url.searchParams.delete('page');
      url.searchParams.set('section_id', 'wh-collection-search-index');
      try {
        const products = await this.products(url);
        if (generation !== this.generation) return;
        const words = this.normalize(query).match(/[a-z0-9]+/g) || [];
        const matches = words.length ? products.map((product) => ({ product, score: this.matchScore(product.terms, words) }))
          .filter(({ score }) => Number.isFinite(score)) : [];
        // Respect an explicit alphabetical/price/date sort; otherwise put exact hits first.
        const sort = url.searchParams.get('sort_by');
        if (!sort || sort === 'best-selling' || sort === 'manual') matches.sort((a, b) => a.score - b.score);
        this.matches = matches.map(({ product }) => product);
        this.status.textContent = this.matches.length
          ? window.whThemeText('wh_search_results', { count: this.matches.length, query })
          : window.whThemeText('wh_search_empty', { query });
        this.shown = 0;
        this.showMore();
      } catch (error) {
        if (generation !== this.generation) return;
        this.status.textContent = window.whThemeText('wh_collection_search_error');
      } finally {
        if (generation === this.generation) this.results.removeAttribute('aria-busy');
      }
    }
    showMore() {
      const fragment = document.createDocumentFragment();
      this.matches.slice(this.shown, this.shown + 24).forEach((product) => {
        const item = document.createElement('li');
        item.className = 'grid__item';
        const link = document.createElement('a');
        link.className = 'wh-category-result';
        link.href = product.url;
        if (product.image) {
          const image = document.createElement('img');
          image.className = 'wh-category-result__image';
          image.src = product.image;
          image.alt = '';
          image.loading = 'lazy';
          image.width = 600;
          image.height = 600;
          link.append(image);
        }
        const text = document.createElement('div');
        text.className = 'wh-category-result__text';
        const title = document.createElement('h3');
        title.textContent = product.title;
        const price = document.createElement('p');
        price.textContent = (product.varies ? window.whThemeText('wh_from_price') + ' ' : '') + product.price;
        text.append(title, price);
        link.append(text);
        item.append(link);
        fragment.append(item);
      });
      this.list.append(fragment);
      this.shown = Math.min(this.shown + 24, this.matches.length);
      this.more.hidden = this.shown >= this.matches.length;
    }
  });
}
